import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getMembers } from '../../../services/organizationService';
import { getOrgWipMaxByUser, getOrgCargoNameByUser } from '../../../services/capacidadeService';
import { ORG_MEMBERS_FULL_QUERY_KEY, TEAM_CAPACITY_WORK_ITEMS_KEY } from './deptUnitAggregates';
import {
  fetchTeamWorkItems,
  computeTeamWorkload,
  computeTeamDashboard,
} from '../../../modules/minha-mesa/services/unifiedWorkService';
import type { TeamDashboardKPIs, TeamMemberWorkload, UnifiedWorkItem } from '../../../modules/minha-mesa/types/workItem';

export interface UseTeamCapacityDataOptions {
  memberUserIds?: string[];
  departmentId?: string;
  /** Atualização periódica para refletir redistribuição de tarefas na UI. */
  refetchInterval?: number;
}

export interface TeamCapacityData {
  items: UnifiedWorkItem[];
  members: TeamMemberWorkload[];
  kpis: TeamDashboardKPIs;
  isLoading: boolean;
}

export function useTeamCapacityData(
  organizationId: string | undefined,
  options?: UseTeamCapacityDataOptions,
): TeamCapacityData {
  const teamQuery = useQuery<UnifiedWorkItem[]>({
    queryKey: TEAM_CAPACITY_WORK_ITEMS_KEY(organizationId ?? ''),
    enabled: Boolean(organizationId),
    staleTime: 15_000,
    refetchOnWindowFocus: true,
    refetchInterval: options?.refetchInterval,
    queryFn: () => fetchTeamWorkItems(organizationId!),
  });

  const orgMembersQuery = useQuery({
    queryKey: ORG_MEMBERS_FULL_QUERY_KEY(organizationId ?? ''),
    enabled: Boolean(organizationId),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    queryFn: () => getMembers(organizationId!),
  });

  const orgMembers = orgMembersQuery.data ?? [];
  const memberUserIds = options?.memberUserIds;
  const departmentId = options?.departmentId;

  const memberIdSet = useMemo(() => {
    if (memberUserIds?.length) return new Set(memberUserIds);
    if (departmentId) {
      const ids = orgMembers
        .filter((m) => m.department_id === departmentId)
        .map((m) => m.user_id);
      return new Set(ids);
    }
    return null;
  }, [memberUserIds, departmentId, orgMembers]);

  const wipMaxQuery = useQuery<Map<string, number>>({
    queryKey: ['org-wip-max', organizationId],
    enabled: Boolean(organizationId),
    staleTime: 120_000,
    queryFn: () => getOrgWipMaxByUser(organizationId!),
  });

  const cargoNameQuery = useQuery<Map<string, string>>({
    queryKey: ['org-cargo-name', organizationId],
    enabled: Boolean(organizationId),
    staleTime: 120_000,
    queryFn: () => getOrgCargoNameByUser(organizationId!),
  });

  const allItems = teamQuery.data ?? [];

  const scopedItems = useMemo(() => {
    if (!memberIdSet) return allItems;
    return allItems.filter((item) => memberIdSet.has(item.assigned_to));
  }, [allItems, memberIdSet]);

  const membersFromItems = useMemo(() => computeTeamWorkload(scopedItems), [scopedItems]);

  const members = useMemo(() => {
    const wipMaxMap = wipMaxQuery.data;
    const cargoNameMap = cargoNameQuery.data;
    const fromItemsMap = new Map(membersFromItems.map((m) => [m.user_id, m]));

    const applyWipOverride = (m: TeamMemberWorkload): TeamMemberWorkload => {
      if (!wipMaxMap) return m;
      const wipMax = wipMaxMap.get(m.user_id);
      if (wipMax != null && m.total_active >= wipMax && m.load_status !== 'overloaded') {
        return { ...m, load_status: 'overloaded' as const };
      }
      return m;
    };

    const relevantMembers = memberIdSet
      ? orgMembers.filter((m) => memberIdSet.has(m.user_id))
      : orgMembers;

    if (!relevantMembers.length) return membersFromItems.map(applyWipOverride);

    return relevantMembers.map((m) => {
      const existing = fromItemsMap.get(m.user_id);
      const cargoName = cargoNameMap?.get(m.user_id);
      const base: TeamMemberWorkload = existing
        ? { ...existing, role_label: cargoName ?? m.role_name ?? existing.role_label }
        : {
            user_id: m.user_id,
            user_name: m.user_name ?? m.user_email ?? m.user_id,
            role_label: cargoName ?? m.role_name ?? undefined,
            total_active: 0,
            tasks_in_progress: 0,
            tasks_pending: 0,
            tasks_overdue: 0,
            tasks_completed_today: 0,
            sla_violated_pct: 0,
            bottleneck_score: 0,
            load_status: 'normal' as const,
          };
      return applyWipOverride(base);
    });
  }, [membersFromItems, orgMembers, wipMaxQuery.data, cargoNameQuery.data, memberIdSet]);

  const kpis = useMemo(() => computeTeamDashboard(scopedItems, members), [scopedItems, members]);

  return {
    items: scopedItems,
    members,
    kpis,
    isLoading: teamQuery.isLoading || orgMembersQuery.isLoading,
  };
}
